import React, {useEffect, useMemo, useRef, useState} from 'react';
import HelpTooltip from '../HelpTooltip';
import OeTip from './OeTip';
import OeSelect from './OeSelect';
import FreightPanel from './FreightPanel';
import ParcelPanel from './ParcelPanel';
import {compareFreight} from './api';
import {fmt, fmt0} from './format';
import {MAX_FREIGHT_POS, MAX_SHIPCOST_LINES, eur, freightComparison, palletEstimate, shipDest} from './freight';

// Versandschritt des Auftrags-Editors (Original `#shipcostbar` /
// `renderShipCostBar` 3001–3085).
//
// Oben der Frachtvergleich (was kostet welcher Weg?), darunter die
// Versandkosten-Position, die tatsächlich auf den Auftrag geschrieben wird.
// Beides gehört zusammen: erst nach der Lagerwahl je Position stehen die
// Gewichte je Lager fest, und erst damit lässt sich die Fracht rechnen.
//
// Die Versandkosten bleiben vorgemerkt (`PENDING_SHIPCOST`) und gehen erst mit
// „Speichern" nach weclapp — dort entsteht je Stück eine eigene Position,
// weil weclapp-Versandkosten keine Menge kennen.

/** Zahlenfeld der Eingabezeile (das Haus verwendet keine Browser-Spinner). */
function NumField({id, value, onChange, ariaLabel, tip, placeholder, disabled}) {
    return (
        <OeTip tip={tip}>
            <input
                id={id}
                inputMode="decimal"
                autoComplete="off"
                aria-label={ariaLabel}
                placeholder={placeholder}
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
            />
        </OeTip>
    );
}

const WEG_TEXT = {lahr: 'alles ab Lahr', snv: 'alles ab SNV', split: '2 Direktlieferungen'};

/** Zeitpunkt der Schätzung in deutscher Zeit (der Server liefert ISO mit Zone). */
function wann(iso) {
    if (!iso) {
        return '';
    }
    return new Date(iso).toLocaleString('de-DE', {
        timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

/**
 * Versandkosten schon vor der AB (Ticket #119): In Schritt 2 wird geschätzt
 * und gemerkt, in Schritt 8 mit dem echten Stand abgeglichen.
 */
function EstimateBox({step, estimate, hasSaved, savedTotal, fcNow, cheapest, pendingDirty, canEdit, onEstimate, onReplace}) {
    const [laeuft, setLaeuft] = useState(false);
    const run = async (payload) => {
        setLaeuft(true);
        try {
            await onEstimate(payload);
        } finally {
            setLaeuft(false);
        }
    };

    if (step === 2) {
        return (
            <div className="srcnote estbox">
                <span>
                    📝 <b>Schätzung für die AB:</b> gerechnet mit den <b>bestellten Mengen</b>, als ob alles
                    lieferbar wäre (Höchstbetrag). In Schritt 8 rechnet der Editor mit dem echten Stand nach.
                    <HelpTooltip
                        id="oe-est-help"
                        placement="top"
                        size={0.7}
                        detail="Fehlmengen werden am Ende in der Rechnung angepasst. Gemerkt werden Betrag, günstigster Weg, Zeitpunkt und Bearbeiter; Schritt 8 zeigt die Abweichung und bietet „ersetzen“ an."
                    >
                        Bei Auslandskunden muss der Versandbetrag mit der AB feststehen.
                    </HelpTooltip>
                </span>
                {estimate ? (
                    <span className="estmerk">
                        ✓ Gemerkt: <b>{eur(estimate.amount)}</b>
                        {estimate.way ? ` (${WEG_TEXT[estimate.way] || estimate.way})` : ''}
                        {' '}· {wann(estimate.at)}{estimate.by ? ` · ${estimate.by}` : ''}
                    </span>
                ) : null}
                {hasSaved && (!estimate || Math.abs(estimate.amount - savedTotal) > 0.005) ? (
                    <OeTip
                        tip={pendingDirty
                            ? 'Erst „Speichern“ — gemerkt wird der Betrag, der in weclapp am Auftrag steht.'
                            : `Merkt ${eur(savedTotal)} als Versandkosten-Schätzung für den Abgleich in Schritt 8.`}
                    >
                        <button
                            type="button"
                            className="actbtn"
                            disabled={laeuft || pendingDirty || !canEdit}
                            onClick={() => run({amount: savedTotal, way: cheapest || null})}
                        >
                            {estimate ? `Schätzung auf ${eur(savedTotal)} ändern` : `Als Schätzung merken (${eur(savedTotal)})`}
                        </button>
                    </OeTip>
                ) : null}
            </div>
        );
    }

    if (step !== 8 || !estimate) {
        return null;
    }

    const neu = fcNow;
    const abw = neu != null ? neu - estimate.amount : null;
    const bestaetigt = !!estimate.confirmedAt;

    return (
        <div className={`srcnote estbox${bestaetigt ? '' : ' warn'}`}>
            <span>
                📝 <b>Schätzung aus Schritt 2:</b> {eur(estimate.amount)}
                {estimate.way ? ` (${WEG_TEXT[estimate.way] || estimate.way})` : ''} · {wann(estimate.at)}
                {estimate.by ? ` · ${estimate.by}` : ''}.
                {' '}Am Auftrag stehen jetzt <b>{eur(savedTotal)}</b>
                {neu != null ? <>, die Palettenfracht mit dem echten Stand ergibt <b>{eur(neu)}</b></> : null}
                {abw != null ? <> — Abweichung <b className={Math.abs(abw) > 0.005 ? 'fwarn' : ''}>
                    {abw > 0 ? '+' : ''}{eur(abw)}</b></> : null}.
            </span>
            {bestaetigt ? (
                <span className="estmerk">✓ Abgeglichen {wann(estimate.confirmedAt)}{estimate.confirmedBy ? ` · ${estimate.confirmedBy}` : ''}</span>
            ) : (
                <span className="estwahl">
                    <OeTip tip={pendingDirty
                        ? 'Erst „Speichern“ — bestätigt wird der Betrag, der in weclapp am Auftrag steht.'
                        : `Bestätigt ${eur(savedTotal)} als endgültige Versandkosten; danach hat Schritt 8 seinen Haken.`}
                    >
                        <button
                            type="button"
                            className="actbtn"
                            disabled={laeuft || pendingDirty || !canEdit}
                            onClick={() => run({amount: savedTotal, confirmed: true})}
                        >
                            ✓ Betrag bestätigen
                        </button>
                    </OeTip>
                    <OeTip
                        tip="Nimmt die vorhandenen Versandkosten zurück; unten neue Zeilen eintragen, speichern und dann bestätigen."
                    >
                        <button type="button" className="actbtn" disabled={!canEdit} onClick={onReplace}>
                            ersetzen
                        </button>
                    </OeTip>
                </span>
            )}
        </div>
    );
}

export default function ShipCostBar({
    step,
    estimate,
    onEstimate,
    mode,
    order,
    rows,
    stock,
    pending,
    canEdit,
    orderRef,
    dispatch,
    onToast,
}) {
    // Eingaben des Frachtvergleichs: `null` heißt „Vorgabe des Rechners".
    const [palletInput, setPalletInput] = useState({total: null, Lahr: null, SNV: null});
    const [sprayOverride, setSprayOverride] = useState(null);

    // Eingabezeile der Versandkosten: `null` heißt „noch nicht angefasst",
    // dann gilt der Vorschlag (Zielland-Artikel, geschätzte Palettenzahl).
    const [artId, setArtId] = useState(null);
    const [qtyText, setQtyText] = useState(null);
    const [priceText, setPriceText] = useState('');
    const [discText, setDiscText] = useState('0');
    const priceRef = useRef(null);

    const [serverAbweichung, setServerAbweichung] = useState(null);

    const artikel = stock.shippingArticles || [];

    const fc = useMemo(
        () => freightComparison(rows, stock, order, palletInput, sprayOverride),
        [rows, stock, order, palletInput, sprayOverride],
    );

    // Gegenprobe im Server: dieselbe Rechnung, andere Sprache. Eine Abweichung
    // über einem Cent ist ein Hinweis auf unterschiedliche Stammdaten und wird
    // sichtbar gemacht, statt still eine der beiden Zahlen zu zeigen.
    useEffect(() => {
        if (!fc || !fc.supported || !stock.shipTo) {
            setServerAbweichung(null);
            return undefined;
        }

        let abgebrochen = false;
        const timer = window.setTimeout(() => {
            compareFreight(orderRef, {
                iso: stock.shipTo.countryCode,
                zip: stock.shipTo.zip,
                weights: {total: fc.sw.total, lahr: fc.sw.Lahr, snv: fc.sw.SNV},
                volumes: {total: fc.sw.vol},
                pallets: {total: fc.pTotal, lahr: fc.pLahr, snv: fc.pSNV},
                spray: fc.spray,
            })
                .then((d) => {
                    if (abgebrochen) {
                        return;
                    }
                    const serverWert = {};
                    for (const w of (d.ways || [])) {
                        serverWert[w.key] = w.total;
                    }
                    const eigen = {lahr: fc.lahr.total, snv: fc.snv.total, split: fc.split.total};
                    const abweichungen = ['lahr', 'snv', 'split']
                        .filter((k) => eigen[k] != null && serverWert[k] != null
                            && Math.abs(eigen[k] - serverWert[k]) > 0.01)
                        .map((k) => `${k === 'lahr' ? 'ab Lahr' : k === 'snv' ? 'ab SNV' : '2 Direktlieferungen'}: `
                            + `hier ${eur(eigen[k])}, Server ${eur(serverWert[k])}`);

                    setServerAbweichung(abweichungen.length
                        ? {tip: `Die Gegenprobe im Server kommt auf andere Beträge — ${abweichungen.join(' · ')}. `
                            + 'Bitte vor dem Verwenden prüfen (meist ein unterschiedlicher Tarifstand).'}
                        : null);
                })
                .catch(() => {
                    // Die Gegenprobe ist Komfort; der Vergleich oben bleibt
                    // ohne sie vollständig bedienbar.
                    if (!abgebrochen) {
                        setServerAbweichung(null);
                    }
                });
        }, 600);

        return () => {
            abgebrochen = true;
            window.clearTimeout(timer);
        };
    }, [fc, orderRef, stock.shipTo]);

    if (mode !== 'order' || !order || !artikel.length) {
        return null;
    }

    const est = palletEstimate(rows, stock);
    const dest = shipDest(stock, order);
    // Palettentyp der Schätzung, der zu diesem Ziel passt (Übersee = Seemaß).
    const useType = est ? (est.types.find((t) => t.key === dest.palletKey) || est.types[0]) : null;
    const destArt = artikel.find((a) => a.articleNumber === dest.articleNr);

    const effArtId = artId != null
        ? artId
        : String((destArt || artikel[0]).articleId);
    const suggQty = useType ? (useType.n || 1) : 1;
    const effQty = qtyText != null ? qtyText : String(suggQty);

    const artOptions = artikel
        .slice()
        .sort((a, b) => String(a.group || '').localeCompare(String(b.group || '')))
        .map((a) => ({
            value: String(a.articleId),
            label: `${a.articleNumber} — ${a.name}`,
            group: a.group || 'Sonstige',
        }));

    // Bei PG1 (Endkunden) behandelt weclapp den Einzelpreis einer Versandkosten-
    // Position als BRUTTO. Beschriftung und Eingabe folgen dem Vertriebsweg.
    const bruttoWege = stock.bruttoVertriebswege || ['GROSS1'];
    const istBrutto = bruttoWege.includes(String(order.salesChannel || '').toUpperCase());
    const preisArt = istBrutto ? 'brutto' : 'netto';

    const zahl = (text) => parseFloat(String(text).replace(',', '.')) || 0;
    const qtyNumber = Math.max(1, parseInt(String(effQty).replace(/[^\d]/g, ''), 10) || 1);
    const summe = qtyNumber * zahl(priceText) * (1 - zahl(discText) / 100);

    // Merkt eine Versandkosten-Zeile vor (Eingabezeile und Paketrechner).
    // Liefert false, wenn die Zeile abgelehnt wurde.
    const vormerken = (art, qty, pricePer, disc) => {
        if (qty > MAX_FREIGHT_POS) {
            onToast(`Anzahl ${fmt0(qty)} zu hoch — max. ${MAX_FREIGHT_POS} Fracht-Positionen. `
                + 'Bitte Anzahl prüfen (meist eine falsche Palettenschätzung).');
            return false;
        }

        dispatch({
            type: 'set-pending',
            patch: {
                shipCost: (pending.shipCost || []).concat([{
                    articleId: art.articleId,
                    articleNumber: art.articleNumber,
                    name: art.name,
                    qty,
                    pricePer,
                    unitPrice: +(qty * pricePer).toFixed(2),
                    discountPercentage: disc,
                }]),
                shipCostTouched: true,
            },
        });

        return true;
    };

    // Paketrechner: legt die Position wie bei den Paletten an. Bei PG1 ist
    // `pricePer` der Bruttopreis aus weclapp, bei allen anderen der Nettopreis.
    // Mehr als `MAX_FREIGHT_POS` Pakete gehen in mehreren Zeilen, weil der
    // Server je Zeile höchstens so viele Einzelpositionen zulässt (und
    // insgesamt 20 Zeilen); diese Menge kommt hier aus der Rechnung und nicht
    // aus einer Schätzung.
    const paketUebernehmen = (zeile) => {
        const zeilen = [];
        for (let rest = zeile.qty; rest > 0; rest -= MAX_FREIGHT_POS) {
            zeilen.push(Math.min(rest, MAX_FREIGHT_POS));
        }

        if ((pending.shipCost || []).length + zeilen.length > MAX_SHIPCOST_LINES) {
            onToast(`Zu viele Versandkosten-Zeilen vorgemerkt (höchstens ${MAX_SHIPCOST_LINES}). `
                + 'Bitte vorhandene Zeilen entfernen oder eine Palette wählen.');
            return;
        }

        dispatch({
            type: 'set-pending',
            patch: {
                shipCost: (pending.shipCost || []).concat(zeilen.map((qty) => ({
                    articleId: zeile.articleId,
                    articleNumber: zeile.articleNumber,
                    name: zeile.name,
                    qty,
                    pricePer: zeile.pricePer,
                    unitPrice: +(qty * zeile.pricePer).toFixed(2),
                    discountPercentage: 0,
                }))),
                shipCostTouched: true,
            },
        });
        onToast(`${fmt0(zeile.qty)}× ${zeile.articleNumber} vorgemerkt — je ${fmt(zeile.pricePer)} € ${preisArt}`
            + (zeilen.length > 1 ? ` (in ${zeilen.length} Zeilen)` : ''));
    };

    const hinzufuegen = () => {
        const art = artikel.find((a) => String(a.articleId) === String(effArtId));
        if (!art) {
            return;
        }
        const preisRoh = String(priceText).replace(',', '.').trim();
        const pricePer = parseFloat(preisRoh);

        if (preisRoh === '' || !(pricePer >= 0)) {
            onToast('Bitte einen Einzelpreis eingeben');
            priceRef.current?.focus();
            return;
        }
        if (vormerken(art, qtyNumber, pricePer, zahl(discText))) {
            setPriceText('');
        }
    };

    const entfernen = (index) => {
        const rest = (pending.shipCost || []).filter((_, i) => i !== index);
        dispatch({
            type: 'set-pending',
            // Ohne vorgemerkte Zeile und ohne bewussten Rückzug gilt der
            // Schritt wieder als unangetastet.
            patch: {shipCost: rest, shipCostTouched: rest.length > 0 || !!pending.shipCostReset},
        });
    };

    // Nimmt alle bereits in weclapp stehenden (und alle vorgemerkten)
    // Versandkosten-Positionen zurück. Einzige Möglichkeit, eine
    // bestehende Zeile zu entfernen - weclapp kennt an ihr keine eigene
    // ID, an der ein einzelner Rückzug ansetzen könnte.
    // Neue Zeilen dürfen danach dazukommen (z. B. 4× FRH1 zu 0 € statt der
    // bisherigen Frachtposition) - sie ersetzen dann die vorhandenen.
    const allesZuruecksetzen = () => dispatch({
        type: 'set-pending',
        patch: {shipCostReset: true, shipCostTouched: true},
    });

    // Vorgemerkter Rückzug der vorhandenen Versandkosten-Positionen.
    const resetPending = !!pending.shipCostReset;

    // Bestehende Versandkosten des Belegs und die vorgemerkten in einer
    // Tabelle — der Bearbeiter sieht auf einen Blick, was schon dransteht.
    const scRows = [];
    for (const s of (order.shippingCosts || [])) {
        scRows.push({
            existing: true,
            removing: resetPending,
            articleNumber: s.articleNumber,
            sum: parseFloat(s.netAmount || 0),
        });
    }
    (pending.shipCost || []).forEach((s, i) => scRows.push({
        articleNumber: s.articleNumber,
        name: s.name,
        qty: s.qty,
        pricePer: s.pricePer,
        disc: s.discountPercentage,
        sum: s.unitPrice * (1 - s.discountPercentage / 100),
        idx: i,
    }));
    const scTotal = scRows.reduce((a, r) => a + (r.removing ? 0 : (r.sum || 0)), 0);

    const overs = (est && est.oversized) || [];

    const savedTotal = (order.shippingCosts || []).reduce((a, sc) => a + parseFloat(sc.netAmount || 0), 0);
    const pendingDirty = (pending.shipCost || []).length > 0 || resetPending;
    // Weg der Schätzung: der günstigste, ohne Wahl das einzige Lager.
    const weg = fc ? (fc.cheapest || ((fc.sw.SNV || 0) > 0 && !(fc.sw.Lahr || 0) ? 'snv' : 'lahr')) : null;
    const fcNow = fc && fc.supported && weg && fc.opts[weg] != null ? fc.opts[weg] : null;

    return (
        <div className="fbar" id="oe-shipcostbar" style={{display: 'flex'}}>
            <div style={{width: '100%'}}>
                {onEstimate ? (
                    <EstimateBox
                        step={step}
                        estimate={estimate}
                        hasSaved={(order.shippingCosts || []).length > 0}
                        savedTotal={savedTotal}
                        fcNow={fcNow}
                        cheapest={weg}
                        pendingDirty={pendingDirty}
                        canEdit={canEdit}
                        onEstimate={onEstimate}
                        onReplace={allesZuruecksetzen}
                    />
                ) : null}
                <FreightPanel
                    fc={fc}
                    shipTo={stock.shipTo}
                    palletInput={palletInput}
                    onPallet={(key, value) => setPalletInput((p) => ({...p, [key]: value}))}
                    sprayOverride={sprayOverride}
                    onSpray={setSprayOverride}
                    serverAbweichung={serverAbweichung}
                    canEdit={canEdit}
                />

                <ParcelPanel
                    order={order}
                    rows={rows}
                    stock={stock}
                    orderRef={orderRef}
                    canEdit={canEdit}
                    onAdopt={paketUebernehmen}
                />

                <div className="scline" style={{marginTop: 10}}>
                    <span className="lbl">
                        Versandkosten:
                        <HelpTooltip
                            id="oe-sc-help"
                            placement="top"
                            size={0.7}
                            detail={<>
                                Wählbar sind nur die freigegebenen Versandartikel (Pflegeseite „Auftrags-Editor —
                                Stammdaten"). In weclapp entsteht je Stück eine eigene Position.
                            </>}
                        >
                            Legt eine Versandkosten-Position am Auftrag an. Sie wird zunächst nur vorgemerkt und
                            geht mit „Speichern" nach weclapp.
                        </HelpTooltip>
                    </span>

                    <OeSelect
                        className="oe-scart"
                        value={effArtId}
                        options={artOptions}
                        onChange={(v) => setArtId(String(v))}
                        disabled={!canEdit}
                        ariaLabel="Versandkosten-Artikel"
                    />

                    <span className="muted">Anzahl</span>
                    <NumField
                        id="oe-scQty2"
                        ariaLabel="Anzahl der Versandkosten-Positionen"
                        tip={`Anzahl Paletten oder Pakete. Höchstens ${MAX_FREIGHT_POS} je Zeile — weclapp legt `
                            + 'für jedes Stück eine eigene Position an.'}
                        value={effQty}
                        disabled={!canEdit}
                        onChange={setQtyText}
                    />

                    <span className="muted">× {preisArt} €</span>
                    <OeTip tip={istBrutto
                        ? 'Einzelpreis BRUTTO je Palette bzw. Paket — bei PG1 (Endkunden) behandelt weclapp den Preis '
                            + 'einer Versandkosten-Position als Bruttopreis. Ohne Preis lässt sich nichts hinzufügen.'
                        : 'Einzelpreis netto je Palette bzw. Paket. Ohne Preis lässt sich nichts hinzufügen.'}>
                        <input
                            id="oe-scPrice2"
                            ref={priceRef}
                            inputMode="decimal"
                            autoComplete="off"
                            aria-label={`Einzelpreis ${preisArt}`}
                            placeholder="z. B. 250"
                            value={priceText}
                            disabled={!canEdit}
                            onChange={(e) => setPriceText(e.target.value)}
                        />
                    </OeTip>

                    <span className="muted">−</span>
                    <NumField
                        id="oe-scDisc2"
                        ariaLabel="Rabatt in Prozent"
                        tip="Rabatt auf die Versandkosten in Prozent (zum Beispiel 50 bei Frachtbeteiligung)."
                        value={discText}
                        disabled={!canEdit}
                        onChange={setDiscText}
                    />
                    <span className="muted">%</span>

                    <span className="muted">= {preisArt}</span>
                    <b>{fmt(summe)} €</b>

                    {canEdit ? (
                        <OeTip tip={'Merkt die Versandkosten-Position vor. Sie erscheint unten in der Liste und geht '
                            + 'erst mit „Speichern" nach weclapp.'}>
                            <button type="button" className="actbtn" onClick={hinzufuegen}>+ hinzufügen</button>
                        </OeTip>
                    ) : null}
                </div>

                <div style={{marginTop: 7, display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap'}}>
                    <OeTip tip={`Ziel ${dest.cc} → ${dest.kind}: `
                        + (dest.kind === 'Inland'
                            ? 'EU-Palette'
                            : dest.kind === 'Ausland'
                                ? 'Einwegpalette (gleiche Größe wie EU)'
                                : 'Seepalette, Übersee-Maß')
                        + `, Artikel ${dest.articleNr}`}>
                        <span className="infochip">🌍 {dest.kind} → <b>{dest.label}</b></span>
                    </OeTip>

                    {est && useType ? (
                        <>
                            <OeTip tip={'Grobe Schätzung aus den Artikelmaßen: Gesamtvolumen geteilt durch '
                                + 'Palettenvolumen (Nutzhöhe × Füllfaktor). Nur ein Vorschlag — der Mitarbeiter '
                                + 'entscheidet.'}>
                                <span className="infochip rec">
                                    📦 Vorschlag: <b>{useType.n}×</b> {dest.label} · Volumen ~{fmt(est.vol)} m³,
                                    {' '}~{fmt0(est.weight)} kg
                                    {est.missing ? ` · ${est.missing} ohne Maße` : ''}
                                </span>
                            </OeTip>
                            {canEdit ? (
                                <OeTip tip={'Trägt die geschätzte Anzahl in das Feld „Anzahl" ein. Hinzugefügt wird damit '
                                    + 'noch nichts.'}>
                                    <button
                                        type="button"
                                        className="pxbtn"
                                        onClick={() => {
                                            setQtyText(String(useType.n));
                                            priceRef.current?.focus();
                                        }}
                                    >
                                        ✓ {useType.n}× übernehmen
                                    </button>
                                </OeTip>
                            ) : null}
                        </>
                    ) : (
                        <span className="muted">Keine Maße hinterlegt — Anzahl bitte selbst schätzen.</span>
                    )}
                </div>

                {overs.length ? (
                    <div className="prodwarn" style={{margin: '8px 0 0'}}>
                        ⚠ <b>{overs.length} Artikel mit unplausiblen Maßen</b> — nicht ins Volumen eingerechnet
                        {' '}(vermutlich falsche Einheit im weclapp-Stammsatz, z. B. cm statt Meter):
                        {' '}{overs.slice(0, 4).map((o) => o.articleNumber + (o.title ? ' · ' + o.title : '')).join(', ')}
                        {overs.length > 4 ? ` … (+${overs.length - 4})` : ''}. Bitte die Anzahl selbst prüfen.
                    </div>
                ) : null}

                {scRows.length ? (
                    <>
                        {canEdit && (order.shippingCosts || []).length ? (
                            <div style={{marginTop: 7}}>
                                {resetPending ? (
                                    <OeTip tip="Nimmt den Rückzug der vorhandenen Versandkosten-Positionen zurück.">
                                        <button
                                            type="button"
                                            className="pxbtn"
                                            onClick={() => dispatch({
                                                type: 'set-pending',
                                                patch: {
                                                    shipCostReset: false,
                                                    shipCostTouched: (pending.shipCost || []).length > 0,
                                                },
                                            })}
                                        >
                                            ↺ Rückzug zurücknehmen
                                        </button>
                                    </OeTip>
                                ) : (
                                    <OeTip
                                        tip={'Entfernt alle bereits am Auftrag stehenden Versandkosten-Positionen; neue Zeilen oben ersetzen sie dann.'}
                                        detail={'Weclapp kennt an ihnen keine eigene ID — deshalb geht nur der Rückzug aller '
                                            + 'auf einmal, nicht einer einzelnen Zeile. Beispiel: 4× FRH1 zu 0 € für frachtfrei. '
                                            + 'Ohne diesen Knopf kommen neue Zeilen zu den vorhandenen dazu.'}
                                    >
                                        <button type="button" className="pxbtn" onClick={allesZuruecksetzen}>
                                            ✕ Alle vorhandenen Versandkosten entfernen
                                        </button>
                                    </OeTip>
                                )}
                            </div>
                        ) : null}
                        <table className="sctable">
                            <thead>
                                <tr>
                                    <th>Versandartikel</th>
                                    <th className="num">Anzahl</th>
                                    <th className="num">Einzelpreis</th>
                                    <th className="num">Rabatt</th>
                                    <th className="num">{istBrutto ? 'Summe brutto' : 'Summe netto'}</th>
                                    <th/>
                                </tr>
                            </thead>
                            <tbody>
                                {scRows.map((r, i) => (r.existing ? (
                                <tr className={r.removing ? 'sc-exist sc-removing' : 'sc-exist'} key={`vorhanden-${i}`}>
                                    <td>
                                        {r.articleNumber} <span className="muted">(am Auftrag)</span>
                                        {r.removing ? <span className="muted"> — wird entfernt</span> : null}
                                    </td>
                                    <td className="num">—</td>
                                    <td className="num">—</td>
                                    <td className="num">—</td>
                                    <td className="num">{r.removing ? <s>{fmt(r.sum)} €</s> : `${fmt(r.sum)} €`}</td>
                                    <td/>
                                </tr>
                            ) : (
                                <tr key={`neu-${r.idx}`}>
                                    <td>
                                        {r.articleNumber}
                                        {r.name ? <span className="muted"> {r.name}</span> : null}
                                    </td>
                                    <td className="num">{fmt0(r.qty)}×</td>
                                    <td className="num">{fmt(r.pricePer)} €</td>
                                    <td className="num">{r.disc > 0 ? `−${fmt0(r.disc)} %` : '—'}</td>
                                    <td className="num"><b>{fmt(r.sum)} €</b></td>
                                    <td className="num">
                                        <OeTip tip="Nimmt diese vorgemerkte Versandkosten-Zeile wieder zurück.">
                                            <button
                                                type="button"
                                                className="pxbtn"
                                                aria-label="Versandkosten-Zeile entfernen"
                                                disabled={!canEdit}
                                                onClick={() => entfernen(r.idx)}
                                            >
                                                ✕
                                            </button>
                                        </OeTip>
                                    </td>
                                </tr>
                            )))}
                                <tr className="sc-total">
                                    <td>{istBrutto ? 'Versandkosten gesamt (am Auftrag netto, vorgemerkt brutto)' : 'Versandkosten gesamt (netto)'}</td>
                                    <td/>
                                    <td/>
                                    <td/>
                                    <td className="num"><b>{fmt(scTotal)} €</b></td>
                                    <td/>
                                </tr>
                            </tbody>
                        </table>
                    </>
                ) : null}
            </div>
        </div>
    );
}
